import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarIcon, ArrowLeft, FileText, Archive, Plus, Download } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Auth } from "@/components/Auth";

interface Order {
  id: string;
  order_date: string;
  isin_code: string;
  customer_portfolio: string;
  shares_quantity: number;
  stock_exchange_code: string;
  order_type: string;
  transaction_type: string;
  validity_date: string;
  status: string;
  created_at: string;
  limit_price?: number;
  user_reference?: string;
  company_name?: string;
  pdf_url?: string;
}

const DBOrder = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  
  // Form state
  const [orderDate, setOrderDate] = useState<Date>();
  const [isinCode, setIsinCode] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [customerPortfolio, setCustomerPortfolio] = useState<string>("");
  const [sharesQuantity, setSharesQuantity] = useState<number>(0);
  const [stockExchangeCode, setStockExchangeCode] = useState("");
  const [orderType, setOrderType] = useState<string>("");
  const [transactionType, setTransactionType] = useState<string>("");
  const [validityDate, setValidityDate] = useState<Date>();
  const [limitPrice, setLimitPrice] = useState<number | undefined>(undefined);
  
  const USER_REFERENCE = "94334";

  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading orders:', error);
    } else {
      setOrders(data || []);
    }
  };

  const resetForm = () => {
    setOrderDate(undefined);
    setIsinCode("");
    setCompanyName("");
    setCustomerPortfolio("");
    setSharesQuantity(0);
    setStockExchangeCode("");
    setOrderType("");
    setTransactionType("");
    setValidityDate(undefined);
    setLimitPrice(undefined);
  };

  const handleSubmit = async () => {
    if (!orderDate || !isinCode || !customerPortfolio || !sharesQuantity || 
        !stockExchangeCode || !orderType || !transactionType || !validityDate) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (orderType === 'Limit' && !limitPrice) {
      toast({
        title: "Missing Limit Price",
        description: "Please enter a limit price for limit orders.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Save to database first
      const orderData = {
        user_id: user.id,
        order_date: format(orderDate, 'yyyy-MM-dd'),
        isin_code: isinCode,
        company_name: companyName || null,
        customer_portfolio: customerPortfolio,
        shares_quantity: sharesQuantity,
        stock_exchange_code: stockExchangeCode,
        order_type: orderType,
        transaction_type: transactionType,
        validity_date: format(validityDate, 'yyyy-MM-dd'),
        status: 'submitted',
        limit_price: orderType === 'Limit' ? limitPrice : null,
        user_reference: USER_REFERENCE,
      };

      const { data: savedOrder, error: saveError } = await supabase
        .from('orders')
        .insert(orderData)
        .select()
        .single();

      if (saveError) throw saveError;

      // Send email with PDF
      const { error: emailError } = await supabase.functions.invoke('send-order-pdf', {
        body: {
          order: {
            ...orderData,
            id: savedOrder.id,
            created_at: savedOrder.created_at,
          },
          recipientEmail: 'jbmeyerposition@gmail.com',
        },
      });

      if (emailError) {
        console.error('Email error:', emailError);
        toast({
          title: "Order Saved",
          description: "Order saved but email could not be sent. Please try again later.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Order submitted and email sent successfully!",
        });
      }

      resetForm();
      loadOrders();
    } catch (error: any) {
      console.error('Error submitting order:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit order.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Auth />;
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">DB Order Management</h1>
        </div>

        <Tabs defaultValue="new-order" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="new-order" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Order
            </TabsTrigger>
            <TabsTrigger value="archives" className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Archives
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new-order">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Create New Order
                  </span>
                  <span className="text-sm font-normal bg-primary/10 text-primary px-3 py-1 rounded-full">
                    USER ID: {USER_REFERENCE}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Order Date */}
                  <div className="space-y-2">
                    <Label>Order Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !orderDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {orderDate ? format(orderDate, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={orderDate}
                          onSelect={setOrderDate}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* ISIN Code */}
                  <div className="space-y-2">
                    <Label>ISIN Code *</Label>
                    <Input
                      placeholder="e.g., US0378331005"
                      value={isinCode}
                      onChange={(e) => setIsinCode(e.target.value.toUpperCase())}
                    />
                  </div>

                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      placeholder="e.g., Apple Inc."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>

                  {/* Customer Portfolio */}
                  <div className="space-y-2">
                    <Label>Customer Portfolio *</Label>
                    <Select value={customerPortfolio} onValueChange={setCustomerPortfolio}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHF">CHF</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Shares Quantity */}
                  <div className="space-y-2">
                    <Label>Shares Quantity *</Label>
                    <Input
                      type="number"
                      placeholder="Enter quantity"
                      value={sharesQuantity || ""}
                      onChange={(e) => setSharesQuantity(parseInt(e.target.value) || 0)}
                    />
                  </div>

                  {/* Stock Exchange Code */}
                  <div className="space-y-2">
                    <Label>Stock Exchange Code *</Label>
                    <Input
                      placeholder="e.g., NYSE, NASDAQ"
                      value={stockExchangeCode}
                      onChange={(e) => setStockExchangeCode(e.target.value.toUpperCase())}
                    />
                  </div>

                  {/* Order Type */}
                  <div className="space-y-2">
                    <Label>Order Type *</Label>
                    <Select value={orderType} onValueChange={setOrderType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select order type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Limit">Limit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Limit Price - only shown for Limit orders */}
                  {orderType === 'Limit' && (
                    <div className="space-y-2">
                      <Label>Limit Price *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter limit price"
                        value={limitPrice || ""}
                        onChange={(e) => setLimitPrice(parseFloat(e.target.value) || undefined)}
                      />
                    </div>
                  )}

                  {/* Transaction Type (Buy/Sell) */}
                  <div className="space-y-2">
                    <Label>Transaction Type *</Label>
                    <Select value={transactionType} onValueChange={setTransactionType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Buy or Sell" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Buy">Buy</SelectItem>
                        <SelectItem value="Sell">Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Validity Date */}
                  <div className="space-y-2">
                    <Label>Time of Validity *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !validityDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {validityDate ? format(validityDate, "PPP") : "Select validity date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={validityDate}
                          onSelect={setValidityDate}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <Button 
                  onClick={handleSubmit} 
                  disabled={loading}
                  className="w-full mt-6"
                  size="lg"
                >
                  {loading ? "Submitting..." : "Submit Order"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="archives">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5" />
                  Order Archives
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No orders found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User ID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>ISIN</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Portfolio</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Exchange</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Limit Price</TableHead>
                          <TableHead>Buy/Sell</TableHead>
                          <TableHead>Valid Until</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>PDF</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow 
                            key={order.id}
                            className={cn(
                              order.pdf_url && "cursor-pointer hover:bg-muted/50"
                            )}
                            onClick={() => {
                              if (order.pdf_url) {
                                window.open(order.pdf_url, '_blank');
                              }
                            }}
                          >
                            <TableCell className="font-mono text-xs">{order.user_reference || '94334'}</TableCell>
                            <TableCell>{format(new Date(order.order_date), 'dd/MM/yyyy')}</TableCell>
                            <TableCell className="font-mono">{order.isin_code}</TableCell>
                            <TableCell>{order.company_name || '-'}</TableCell>
                            <TableCell>{order.customer_portfolio}</TableCell>
                            <TableCell>{order.shares_quantity}</TableCell>
                            <TableCell>{order.stock_exchange_code}</TableCell>
                            <TableCell>{order.order_type}</TableCell>
                            <TableCell>{order.limit_price ? order.limit_price.toFixed(2) : '-'}</TableCell>
                            <TableCell>
                              <span className={cn(
                                "px-2 py-1 rounded text-xs font-medium",
                                order.transaction_type === 'Buy' 
                                  ? "bg-green-100 text-green-800" 
                                  : "bg-red-100 text-red-800"
                              )}>
                                {order.transaction_type}
                              </span>
                            </TableCell>
                            <TableCell>{format(new Date(order.validity_date), 'dd/MM/yyyy')}</TableCell>
                            <TableCell>
                              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {order.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              {order.pdf_url ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(order.pdf_url, '_blank');
                                  }}
                                >
                                  <Download className="h-4 w-4 text-primary" />
                                </Button>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DBOrder;
